import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  AlertTriangle, XCircle, Search, User, Users, 
  Check, X, Loader2, Link2, Copy
} from 'lucide-react';

export function ImportReview({ onResolve }) {
  const [loading, setLoading] = useState(true);
  const [titles, setTitles] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [searchStudent, setSearchStudent] = useState('');
  const [searchGuardian, setSearchGuardian] = useState('');
  const [searchResults, setSearchResults] = useState({ students: [], guardians: [] });
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    loadPendingReviews();
  }, []);

  const loadPendingReviews = async () => {
    try {
      const res = await api.get('/finance/import-titles/pending-reviews');
      setTitles(res.data.titles || []);
    } catch (error) {
      console.error('Erro ao carregar pendências:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchEntities = async () => {
    if (!searchStudent && !searchGuardian) return;
    
    setSearching(true);
    try {
      const results = { students: [], guardians: [] };
      
      if (searchStudent) {
        const res = await api.get(`/students?search=${encodeURIComponent(searchStudent)}&limit=5`);
        results.students = res.data.students || res.data || [];
      }
      
      if (searchGuardian) {
        const res = await api.get(`/responsibles?search=${encodeURIComponent(searchGuardian)}&limit=5`);
        results.guardians = res.data.responsibles || res.data || [];
      }
      
      setSearchResults(results);
    } catch (error) {
      console.error('Erro na busca:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleResolveLink = async (studentId, guardianId) => {
    if (!selectedTitle) return;
    
    setResolving(true);
    try {
      await api.patch(`/finance/import-titles/items/${selectedTitle.id}/resolve-link`, null, {
        params: { student_id: studentId, guardian_id: guardianId }
      });
      
      toast.success('Vínculo resolvido com sucesso!');
      setSelectedTitle(null);
      loadPendingReviews();
      onResolve?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resolver vínculo');
    } finally {
      setResolving(false);
    }
  };

  const handleResolveDuplicate = async (titleId, action) => {
    setResolving(true);
    try {
      await api.patch(`/finance/import-titles/items/${titleId}/resolve-duplicate?action=${action}`);
      
      toast.success(`Duplicidade resolvida: ${action === 'keep' ? 'mantido' : 'ignorado'}`);
      loadPendingReviews();
      onResolve?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resolver duplicidade');
    } finally {
      setResolving(false);
    }
  };

  const getMatchingIcon = (status) => {
    switch (status) {
      case 'guardian_not_found':
      case 'student_not_found':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'multiple_matches':
      case 'conflict':
      case 'pending_review':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-slate-500" />;
    }
  };

  const groupedTitles = {
    link: titles.filter(t => ['guardian_not_found', 'student_not_found', 'multiple_matches', 'conflict'].includes(t.matching_status)),
    duplicate: titles.filter(t => t.duplicate_status === 'exact' || t.duplicate_status === 'possible')
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Link Issues */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-yellow-500" />
            Pendências de Vínculo
          </CardTitle>
          <CardDescription>
            Títulos que precisam de vinculação manual com aluno ou responsável
          </CardDescription>
        </CardHeader>
        <CardContent>
          {groupedTitles.link.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma pendência encontrada.
            </p>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {groupedTitles.link.map((title) => (
                  <div 
                    key={title.id}
                    className="p-4 rounded-lg border bg-yellow-50 border-yellow-200"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {getMatchingIcon(title.matching_status)}
                          <span className="font-medium">{title.student_name}</span>
                          <Badge variant="secondary" className="text-xs">Linha {title.row_number}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Responsável: {title.guardian_name || 'Não informado'} | 
                          CPF: {title.guardian_document || 'Não informado'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Valor: R$ {title.amount_original?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | 
                          Categoria: {title.category_external}
                        </div>
                        {title.error_message && (
                          <p className="text-sm text-red-600">{title.error_message}</p>
                        )}
                      </div>
                      <Button 
                        size="sm"
                        onClick={() => {
                          setSelectedTitle(title);
                          setSearchStudent(title.student_name);
                          setSearchGuardian(title.guardian_name);
                        }}
                      >
                        Resolver
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Duplicate Issues */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-orange-500" />
            Duplicidades
          </CardTitle>
          <CardDescription>
            Títulos que podem ser duplicados de registros existentes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {groupedTitles.duplicate.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma duplicidade encontrada.
            </p>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {groupedTitles.duplicate.map((title) => (
                  <div 
                    key={title.id}
                    className="p-4 rounded-lg border bg-orange-50 border-orange-200"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Copy className="w-5 h-5 text-orange-500" />
                          <span className="font-medium">{title.student_name}</span>
                          <Badge variant={title.duplicate_status === 'exact' ? 'destructive' : 'warning'} className="text-xs">
                            {title.duplicate_status === 'exact' ? 'Duplicado Exato' : 'Possível Duplicado'}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Valor: R$ {title.amount_original?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | 
                          Vencimento: {title.due_date ? (() => { const m = String(title.due_date).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : 'N/A'; })() : 'N/A'}
                        </div>
                        {title.duplicate_reason && (
                          <p className="text-sm text-orange-600">{title.duplicate_reason}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => handleResolveDuplicate(title.id, 'skip')}
                          disabled={resolving}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Ignorar
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => handleResolveDuplicate(title.id, 'keep')}
                          disabled={resolving}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Manter
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Link Resolution Dialog */}
      <Dialog open={!!selectedTitle} onOpenChange={(open) => !open && setSelectedTitle(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Resolver Vínculo</DialogTitle>
          </DialogHeader>
          
          {selectedTitle && (
            <div className="space-y-4">
              {/* Title Info */}
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="font-medium">{selectedTitle.student_name}</p>
                <p className="text-sm text-muted-foreground">
                  Responsável: {selectedTitle.guardian_name} | CPF: {selectedTitle.guardian_document}
                </p>
              </div>

              {/* Search */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Buscar Aluno</Label>
                  <div className="flex gap-2">
                    <Input
                      value={searchStudent}
                      onChange={(e) => setSearchStudent(e.target.value)}
                      placeholder="Nome do aluno"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Buscar Responsável</Label>
                  <div className="flex gap-2">
                    <Input
                      value={searchGuardian}
                      onChange={(e) => setSearchGuardian(e.target.value)}
                      placeholder="Nome ou CPF"
                    />
                  </div>
                </div>
              </div>
              
              <Button onClick={searchEntities} disabled={searching} className="w-full">
                {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Buscar
              </Button>

              {/* Results */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Alunos Encontrados</Label>
                  <ScrollArea className="h-[200px] border rounded-lg p-2">
                    {searchResults.students.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">Nenhum aluno encontrado</p>
                    ) : (
                      searchResults.students.map((student) => (
                        <div 
                          key={student.id}
                          className="p-2 hover:bg-slate-100 rounded cursor-pointer"
                          onClick={() => handleResolveLink(student.id, selectedTitle.guardian_id)}
                        >
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-purple-500" />
                            <span className="font-medium text-sm">{student.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground ml-6">
                            {student.class_group_name || student.class_name || 'Sem turma'}
                          </p>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Responsáveis Encontrados</Label>
                  <ScrollArea className="h-[200px] border rounded-lg p-2">
                    {searchResults.guardians.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">Nenhum responsável encontrado</p>
                    ) : (
                      searchResults.guardians.map((guardian) => (
                        <div 
                          key={guardian.id}
                          className="p-2 hover:bg-slate-100 rounded cursor-pointer"
                          onClick={() => handleResolveLink(selectedTitle.student_id, guardian.id)}
                        >
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-green-500" />
                            <span className="font-medium text-sm">{guardian.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground ml-6">
                            CPF: {guardian.cpf || guardian.document || 'Não informado'}
                          </p>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTitle(null)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
